import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Simple Cell Renderer Component
 *
 * Renders simple string or number values with consistent styling.
 * Fallback renderer for fields that don't need special formatting.
 *
 * Features:
 * - Displays any primitive value (string, number, boolean)
 * - Consistent text styling
 * - Falls back to "-" for null/undefined values
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'codeReason',
 *   headerName: 'Tipo',
 *   cellRenderer: SimpleCellRendererComponent,
 *   sortable: true
 * }
 * ```
 *
 * Output examples:
 * - "FAC" - Invoice type
 * - "NCR" - Credit note
 * - "123" - Any number
 */
@Component({
  selector: 'app-simple-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-content">
      {{ displayValue }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    .text-content {
      font-size: 12px;
      line-height: normal;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Get display value
   */
  get displayValue(): string {
    const value = this.params?.value;
    
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    return String(value);
  }
}
